import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, 
  Plus, 
  Edit2, 
  Trash2, 
  ShoppingBag,
  Download,
  Calendar,
  DollarSign,
  Image as ImageIcon,
  Loader2,
  ChevronRight,
  BarChart3,
  Clock,
  Tag
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { resolveImageUrl } from '@/utils/imageUrl';
import { OptimizedImage } from '@/components/OptimizedImage';

const API = `${window.location.origin}/api`;

const PRODUCT_TYPES = [
  { id: 'physical', label: 'Physical Product', icon: Package, description: 'Tangible goods that require shipping' },
  { id: 'digital', label: 'Digital Product', icon: Download, description: 'Downloads, files, or digital access' },
  { id: 'service', label: 'Service', icon: Calendar, description: 'Bookable services or appointments' }
];

const BOOKING_TYPES = [
  { id: 'contact', label: 'Contact to Book', description: 'Customers reach out directly' },
  { id: 'calendar', label: 'Calendar Booking', description: 'Customers pick available time slots' }
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export default function BusinessProducts({ businessId, isOwner }) {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  
  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    product_type: 'physical',
    category: '',
    images: [],
    inventory_count: '',
    weight: '',
    download_url: '',
    duration_minutes: '60',
    booking_type: 'contact',
    available_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    available_hours: { start: '09:00', end: '17:00' }
  });

  useEffect(() => {
    fetchProducts();
    if (isOwner) {
      fetchStats();
    }
  }, [businessId]);

  const fetchProducts = async () => {
    try {
      const type = activeTab !== 'all' ? `?product_type=${activeTab}` : '';
      const res = await fetch(`${API}/businesses/${businessId}/products${type}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/businesses/${businessId}/sales-stats`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleOpenCreate = () => {
    setEditing(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      product_type: 'physical',
      category: '',
      images: [],
      inventory_count: '',
      weight: '',
      download_url: '',
      duration_minutes: '60',
      booking_type: 'contact',
      available_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      available_hours: { start: '09:00', end: '17:00' }
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (product) => {
    setEditing(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      product_type: product.product_type,
      category: product.category || '',
      images: product.images || [],
      inventory_count: product.inventory_count?.toString() || '',
      weight: product.weight?.toString() || '',
      download_url: product.download_url || '',
      duration_minutes: product.duration_minutes?.toString() || '60',
      booking_type: product.booking_type || 'contact',
      available_days: product.available_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      available_hours: product.available_hours || { start: '09:00', end: '17:00' }
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.price) {
      toast.error('Name and price are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        product_type: formData.product_type,
        category: formData.category || null,
        images: formData.images
      };

      // Add type-specific fields
      if (formData.product_type === 'physical') {
        payload.inventory_count = formData.inventory_count ? parseInt(formData.inventory_count) : null;
        payload.weight = formData.weight ? parseFloat(formData.weight) : null;
      } else if (formData.product_type === 'digital') {
        payload.download_url = formData.download_url;
      } else if (formData.product_type === 'service') {
        payload.duration_minutes = parseInt(formData.duration_minutes);
        payload.booking_type = formData.booking_type;
        payload.available_days = formData.available_days;
        payload.available_hours = formData.available_hours;
      }

      const url = editing 
        ? `${API}/products/${editing.product_id}`
        : `${API}/businesses/${businessId}/products`;
      
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(editing ? 'Product updated!' : 'Product created!');
        setDialogOpen(false);
        fetchProducts();
      } else {
        toast.error(data.detail || 'Failed to save product');
      }
    } catch (error) {
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    try {
      const res = await fetch(`${API}/products/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Product deleted');
        fetchProducts();
      } else {
        toast.error('Failed to delete product');
      }
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const res = await fetch(`${API}/upload/image`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });

      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, data.url]
        }));
        toast.success('Image uploaded');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      available_days: prev.available_days.includes(day)
        ? prev.available_days.filter(d => d !== day)
        : [...prev.available_days, day]
    }));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-20 skeleton rounded-xl" />
        <div className="h-40 skeleton rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Section (Owner Only) */}
      {isOwner && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
              <ShoppingBag className="w-4 h-4" />
              Total Orders
            </div>
            <p className="text-2xl font-bold text-white">{stats.summary?.total_orders || 0}</p>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
              <DollarSign className="w-4 h-4" />
              Revenue
            </div>
            <p className="text-2xl font-bold text-white">{(stats.summary?.total_revenue || 0).toFixed(0)} MEL</p>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
              <Tag className="w-4 h-4" />
              Platform Fees
            </div>
            <p className="text-2xl font-bold text-zinc-400">{(stats.summary?.total_fees || 0).toFixed(0)} MEL</p>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
              <BarChart3 className="w-4 h-4" />
              Your Earnings
            </div>
            <p className="text-2xl font-bold text-green-400">{(stats.summary?.total_earnings || 0).toFixed(0)} MEL</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {['all', 'physical', 'digital', 'service'].map(tab => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); fetchProducts(); }}
              className={`px-4 py-2 rounded-lg text-sm capitalize transition-colors ${
                activeTab === tab 
                  ? 'bg-white text-black' 
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
            >
              {tab === 'all' ? 'All' : tab}
            </button>
          ))}
        </div>

        {isOwner && (
          <button
            onClick={handleOpenCreate}
            className="btn-primary flex items-center gap-2"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </button>
        )}
      </div>

      {/* Products Grid */}
      {products.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(product => (
            <motion.div
              key={product.product_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card rounded-xl overflow-hidden group"
            >
              {/* Image */}
              <div className="aspect-video bg-zinc-900 relative">
                {product.images?.[0] ? (
                  <OptimizedImage 
                    src={product.images[0]}
                    alt={product.name}
                    preset="card"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-12 h-12 text-zinc-700" />
                  </div>
                )}
                
                {/* Type Badge */}
                <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/70 text-xs text-white capitalize">
                  {product.product_type}
                </div>

                {/* Owner Actions */}
                {isOwner && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleOpenEdit(product)}
                      className="p-2 rounded-lg bg-black/70 text-white hover:bg-black"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(product.product_id)}
                      className="p-2 rounded-lg bg-red-500/70 text-white hover:bg-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-white mb-1">{product.name}</h3>
                <p className="text-zinc-400 text-sm line-clamp-2 mb-3">{product.description}</p>
                
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-green-400">{product.price} MEL</span>
                  
                  {product.product_type === 'physical' && product.inventory_count !== null && (
                    <span className="text-xs text-zinc-500">
                      {product.inventory_count} in stock
                    </span>
                  )}
                  
                  {product.product_type === 'service' && (
                    <span className="text-xs text-zinc-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {product.duration_minutes} min
                    </span>
                  )}
                </div>

                {isOwner && (
                  <div className="mt-3 pt-3 border-t border-zinc-800 text-xs text-zinc-500">
                    {product.total_sales || 0} sales · {product.total_revenue?.toFixed(0) || 0} MEL revenue
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No Products Yet</h3>
          <p className="text-zinc-400 mb-6">
            {isOwner ? "Add your first product or service to start selling." : "This business hasn't added any products yet."}
          </p>
          {isOwner && (
            <button onClick={handleOpenCreate} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add First Product
            </button>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editing ? 'Edit Product' : 'Add New Product'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {editing ? 'Update your product details' : 'Choose a type and fill in the details'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Product Type Selection */}
            {!editing && (
              <div>
                <label className="text-zinc-400 text-sm mb-3 block">Product Type</label>
                <div className="grid grid-cols-3 gap-3">
                  {PRODUCT_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setFormData(prev => ({ ...prev, product_type: type.id }))}
                      className={`p-4 rounded-xl border text-left transition-colors ${
                        formData.product_type === type.id
                          ? 'bg-purple-500/10 border-purple-500/50'
                          : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
                      }`}
                    >
                      <type.icon className={`w-6 h-6 mb-2 ${
                        formData.product_type === type.id ? 'text-purple-400' : 'text-zinc-400'
                      }`} />
                      <p className="font-medium text-white text-sm">{type.label}</p>
                      <p className="text-zinc-500 text-xs mt-1">{type.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-zinc-400 text-sm mb-2 block">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Product name"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>

              <div className="col-span-2">
                <label className="text-zinc-400 text-sm mb-2 block">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your product..."
                  rows={3}
                  className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Price (MEL) *</label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="0.00"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>

              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Category</label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="e.g., Clothing, Food"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>
            </div>

            {/* Images */}
            <div>
              <label className="text-zinc-400 text-sm mb-2 block">Images</label>
              <div className="flex gap-3 flex-wrap">
                {formData.images.map((url, idx) => (
                  <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden">
                    <OptimizedImage src={url} alt="" preset="thumb" className="w-full h-full object-cover" />
                    <button
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        images: prev.images.filter((_, i) => i !== idx)
                      }))}
                      className="absolute top-1 right-1 p-1 rounded bg-red-500 text-white"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 rounded-lg border-2 border-dashed border-zinc-700 flex items-center justify-center cursor-pointer hover:border-zinc-500">
                  <ImageIcon className="w-6 h-6 text-zinc-500" />
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                </label>
              </div>
            </div>

            {/* Type-specific fields */}
            {formData.product_type === 'physical' && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-900/50 rounded-xl">
                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Inventory Count</label>
                  <Input
                    type="number"
                    value={formData.inventory_count}
                    onChange={(e) => setFormData(prev => ({ ...prev, inventory_count: e.target.value }))}
                    placeholder="Optional"
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Weight (lbs)</label>
                  <Input
                    type="number"
                    value={formData.weight}
                    onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                    placeholder="For shipping"
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
              </div>
            )}

            {formData.product_type === 'digital' && (
              <div className="p-4 bg-zinc-900/50 rounded-xl">
                <label className="text-zinc-400 text-sm mb-2 block">Download URL</label>
                <Input
                  value={formData.download_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, download_url: e.target.value }))}
                  placeholder="https://..."
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
                <p className="text-zinc-500 text-xs mt-1">URL where customers can download the product</p>
              </div>
            )}

            {formData.product_type === 'service' && (
              <div className="space-y-4 p-4 bg-zinc-900/50 rounded-xl">
                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Duration (minutes)</label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: e.target.value }))}
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>

                <div>
                  <label className="text-zinc-400 text-sm mb-3 block">Booking Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    {BOOKING_TYPES.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setFormData(prev => ({ ...prev, booking_type: type.id }))}
                        className={`p-3 rounded-lg border text-left ${
                          formData.booking_type === type.id
                            ? 'bg-green-500/10 border-green-500/50'
                            : 'bg-zinc-800 border-zinc-700'
                        }`}
                      >
                        <p className="font-medium text-white text-sm">{type.label}</p>
                        <p className="text-zinc-500 text-xs">{type.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {formData.booking_type === 'calendar' && (
                  <>
                    <div>
                      <label className="text-zinc-400 text-sm mb-2 block">Available Days</label>
                      <div className="flex flex-wrap gap-2">
                        {DAYS.map(day => (
                          <button
                            key={day}
                            onClick={() => toggleDay(day)}
                            className={`px-3 py-1 rounded-full text-sm capitalize ${
                              formData.available_days.includes(day)
                                ? 'bg-purple-500 text-white'
                                : 'bg-zinc-800 text-zinc-400'
                            }`}
                          >
                            {day.slice(0, 3)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-zinc-400 text-sm mb-2 block">Start Time</label>
                        <Input
                          type="time"
                          value={formData.available_hours.start}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            available_hours: { ...prev.available_hours, start: e.target.value }
                          }))}
                          className="bg-zinc-900 border-zinc-800 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-zinc-400 text-sm mb-2 block">End Time</label>
                        <Input
                          type="time"
                          value={formData.available_hours.end}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            available_hours: { ...prev.available_hours, end: e.target.value }
                          }))}
                          className="bg-zinc-900 border-zinc-800 text-white"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <button
              onClick={() => setDialogOpen(false)}
              className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-500 disabled:opacity-50"
              data-testid="save-product-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Saving...
                </>
              ) : (
                editing ? 'Update Product' : 'Create Product'
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
